import type { MeetingDetail, PublicMeeting } from "src/api/meetingsApi";

const SCHEDULED_START_EARLY_HOURS = 12;

const formatMeetingDateTime = (value: string | null | undefined, timezone: string): string | null => {
    if (!value) {
        return null;
    }
    return new Intl.DateTimeFormat(undefined, {
        dateStyle: "full",
        timeStyle: "short",
        timeZone: timezone,
    }).format(new Date(value));
};

export const getHostStartRestriction = (meeting: MeetingDetail | null, now: number): string | null => {
    if (!meeting?.canManage || meeting.occurrence.status !== "scheduled") {
        return null;
    }
    const meetingStartValue = meeting.occurrence.scheduledStart || meeting.scheduledStart;
    const earliestStartValue = meeting.earliestStartAt || (meetingStartValue
        ? new Date(new Date(meetingStartValue).getTime() - SCHEDULED_START_EARLY_HOURS * 60 * 60 * 1000).toISOString()
        : null);
    if (!earliestStartValue || now >= new Date(earliestStartValue).getTime()) {
        return null;
    }

    const meetingStart = formatMeetingDateTime(meetingStartValue, meeting.timezone);
    const earliestAllowedStart = formatMeetingDateTime(earliestStartValue, meeting.timezone);
    if (meetingStart && earliestAllowedStart) {
        return `This meeting is scheduled for ${meetingStart} and cannot be started earlier than ${earliestAllowedStart}.`;
    }
    return meetingStart
        ? `This meeting is scheduled for ${meetingStart} and cannot be started yet.`
        : "This meeting cannot be started yet.";
};

export const getAttendeeScheduleNotice = (meeting: PublicMeeting | null, now: number): string | null => {
    if (!meeting?.scheduledStart || meeting.status !== "scheduled") {
        return null;
    }
    const scheduledStartTime = new Date(meeting.scheduledStart).getTime();
    if (now >= scheduledStartTime) {
        return null;
    }

    const scheduledStart = formatMeetingDateTime(meeting.scheduledStart, meeting.timezone);
    const earliestStartValue = new Date(
        scheduledStartTime - SCHEDULED_START_EARLY_HOURS * 60 * 60 * 1000
    ).toISOString();
    const earliestAllowedStart = formatMeetingDateTime(earliestStartValue, meeting.timezone);
    if (now < new Date(earliestStartValue).getTime() && scheduledStart && earliestAllowedStart) {
        return `This meeting is scheduled for ${scheduledStart}. The host can start it from ${earliestAllowedStart}. You can join after the host starts the meeting.`;
    }
    return scheduledStart
        ? `This meeting is scheduled for ${scheduledStart}. You can join after the host starts the meeting.`
        : "You can join after the host starts the meeting.";
};
